<template>
  <div class="h-100">
    <div class="row">
      <div class="col-12 mb-2">
        <span class="d-inline font-weight-bold">Viewing advances for:</span>
        <span class="d-inline ml-2">{{ selectedDate | longDate }}</span>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <span class="d-inline pr-1">Only show times with no reads</span>
        <span class="d-inline">
          <label class="checkbox">
            <input
              type="checkbox"
              @click="onlyShowNoReads = !onlyShowNoReads"
            />
            <span class="default"></span>
          </label>
        </span>
      </div>
    </div>
    <!-- Tables For All Time Periods -->
    <div class="row" v-if="!onlyShowNoReads">
      <div class="col-6" v-for="table in 2" :key="table">
        <b-table
          class="table-scroll mt-3"
          small
          striped
          text-center
          :items="tableDataFull[table]"
          :fields="summaryConverterFields2"
          :per-page="24"
        >
          <template slot="TimeOfDay" slot-scope="data">{{
            data.item.time
          }}</template>
          <template slot="Advances" slot-scope="data">
            {{ formatAdvanceValue(data.item.value, data.item.isActual).display }}
            <span>{{ formatAdvanceValue(data.item.value, data.item.isActual).suffix }}</span>
          </template>
        </b-table>
      </div>
    </div>
    <!-- Tables For All Time Periods End-->
    <!-- Tables For Time Periods With No Reads-->
    <div class="row" v-else-if="advancesWithNoReads.length > 0">
      <div class="col-6" v-for="table in 2" :key="table">
        <!-- Table 1 For Time Periods With No Reads -->
        <b-table
          class="table-scroll mt-3"
          striped
          text-center
          :items="tableDataOnlyMissing[table]"
          :fields="summaryConverterFields2"
          :per-page="24"
          v-if="advancesWithNoReads.length > 24 * (table - 1)"
        >
          <template slot="TimeOfDay" slot-scope="data">{{
            data.item.time
          }}</template>
          <template slot="Advances">n/a</template>
        </b-table>
        <!-- Table 1 For Time Periods With No Reads End-->
      </div>
    </div>
    <p class="mt-3 font-weight-bold" v-else>
      All periods have reads for this day
    </p>
    <!-- Tables For Time Periods With No Reads End -->
  </div>
</template>

<script>
import moment from 'moment';
import _filters from '@/mixins/filters.js';

export default {
	name: 'advances-table',
	props: {
		selectedDate: {},
	},
	mixins: [_filters],
	data() {
		return {
			onlyShowNoReads: false,
			summaryFields: {
				ReadingDate: {
					label: 'Date',
					sortable: true,
					class: 'text-center',
				},
				Serial: {
					label: 'Meter Serial',
					class: 'text-center',
				},
				ReadingType: {
					label: 'Reading Type',
					class: 'text-center',
				},
				MeterReading: {
					label: 'Reading',
					class: 'text-center',
				},
				AssociatedInvoices: {
					label: 'Billing Info',
					class: 'text-center',
				},
			},
			summaryConverterFields: {
				ReadingDate: {
					label: 'Date',
					sortable: true,
					class: 'text-center',
				},
				Serial: {
					label: 'Meter Serial',
					class: 'text-center',
				},
				ReadingType: {
					label: 'Reading Type',
					class: 'text-center',
				},
				MeterReading: {
					label: 'Meter Reading',
					class: 'text-center',
				},
				ConverterUncorrectedReading: {
					label: 'Uncorrected Reading',
					class: 'text-center',
				},
				ConverterCorrectedReading: {
					label: 'Corrected Reading',
					class: 'text-center',
				},
				AssociatedInvoices: {
					label: 'Billing Info',
					class: 'text-center',
				},
			},
			summaryFields2: {
				TimeOfDay: {
					label: 'Time of day',
					sortable: true,
					class: 'col-6',
				},
				Advances: {
					label: 'Advances',
					class: 'col-6',
				},
			},
			summaryConverterFields2: {
				TimeOfDay: {
					label: 'Time of day',
					sortable: true,
					class: 'col-5',
				},
				Advances: {
					label: 'Advances',
					class: 'col',
				},
			},
		};
	},
	methods: {
		splitAdvances(tableNum, advances) {
			let splitAdvances = [advances.slice(0, 24), advances.slice(24, 48)];
			return splitAdvances[tableNum - 1];
		},

    toTime(period) {
      if (period > 48) return "Invalid period";
      let hours = (period - 1) / 2;
      let time = "";
      if (hours % 1 === 0) {
        time = hours + ":" + "00";
      } else {
        let hour = hours.toString().split(".");
        time = hour[0] + ":" + "30";
      }
      if (time.length === 4) {
        return "0" + time;
      } else {
        return time;
      }
    },

    formatAdvanceValue(value, isActual) {
      // Check if value is a valid number (including 0)
      if (typeof value === 'number' && !isNaN(value)) {
        return {
          display: value.toFixed(2).toString(),
          suffix: isActual ? "" : "(E)"
        };
      }
      return { display: "n/a", suffix: "" };
    },
  },
  computed: {
    shouldShowAdvances() {
      // Use definitive logic: customer has ever been HH AND advance ranges are available
      return this.$store.getters.hasEverBeenHH && 
             this.$store.state.meters.advanceRangesAvailable === true;
    },
    HhMeterPoint() {
      // Backward compatibility - return object with AdvancesUntil if advances should be shown
      return this.shouldShowAdvances ? {
        AdvancesUntil: this.advancesUntil
      } : null;
    },
    advancesUntil() {
      // Calculate AdvancesUntil from the advance ranges
      const ranges = this.$store.state.meters.advanceRAGRanges;
      if (!ranges || ranges.length === 0) return null;
      
      // Find the latest end date from all advance ranges
      const latestEndDate = ranges.reduce((latest, range) => {
        const endDate = moment(range.RangeEnd);
        return !latest || endDate.isAfter(latest) ? endDate : latest;
      }, null);
      
      return latestEndDate ? latestEndDate.format() : null;
    },
    supplyStartDate() {
      // Use Registration data from FlowBot instead of AdvancesBot HhMeterPoint
      const registration = this.$store.state.meters.activeService.Registration;
      return registration && registration.SupplyStartDate 
        ? moment(registration.SupplyStartDate)
        : moment(); // Fallback to current date
    },
    advances() {
		let that = this;
		let advances = this.$_.find(
			this.$store.state.meters.advances,
			function (advance) {
			return moment(that.selectedDate).isSame(advance.AdvanceDate, "day");
			}
		);

		if (advances)
			return advances.ActiveImportPeriods;
		else 
			return [];
	},

	advancesMapped() {
		if (this.advances) {
			return this.advances.map((advance) => ({
				time: this.toTime(advance.PeriodId),
				value: advance.Value,
				isActual: advance.IsActual,
				period: advance.PeriodId,
			}));
		} else {
			return undefined;
		}
	},

	tableDataFull() {
		return {
			1: this.splitAdvances(1, this.allPeriods),
			2: this.splitAdvances(2, this.allPeriods),
		};
	},

	tableDataOnlyMissing() {
		return {
			1: this.splitAdvances(1, this.periodsWithNoData),
			2: this.splitAdvances(2, this.periodsWithNoData),
		};
	},

	allPeriods() {
		// Create array of all 48 half-hour periods, filling missing periods with null
		let advances = [];

		for (let period = 1; period < 49; period++) {
			let advance = this.advancesMapped.find((a) => a.period == period);
			advances.push(
				advance || {
					time: this.toTime(period),
					value: null,
					period: period,
				}
			);
		}

		return advances;
	},

	periodsWithNoData() {
		return this.allPeriods.filter((item) => item.value == null);
	},
	
	},
};
</script>
